import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../auth/useAuth'
import { AUTH_FAILED_LOGIN_PATH, AUTH_PATHS } from '../lib/routes'
import type { AccessDeniedState } from './AccessDenied'

/** A failed sign-in goes back to the landing card, which shows the
 * prototype's "Authentication failed" line (MS-993). A full page load, so the
 * Gate re-reads the (absent) session and renders Login as on any signed-out
 * visit. */
function returnToLoginWithAuthFailed() {
  globalThis.location.replace(AUTH_FAILED_LOGIN_PATH)
}

/** `/oauth2/callback`: Cognito's redirect back after the managed login
 * (MS-977, MS-997). Exchanges the code for the pending login and goes on to
 * the account step; there is no session until SelectAccount's POST
 * /select-account mints one, so nothing here calls login(). */
export function OAuthCallback() {
  const { t } = useTranslation()
  const { logout } = useAuth()
  const navigate = useNavigate()
  const exchanged = useRef(false)

  useEffect(() => {
    if (exchanged.current) return
    exchanged.current = true

    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    const state = params.get('state')

    if (!code || !state) {
      // An error from the identity provider (?error=...) or a bare visit.
      returnToLoginWithAuthFailed()
      return
    }

    fetch('/api/v1/auth/exchange', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, state }),
    })
      .then(async (resp) => {
        const body = (await resp.json().catch(() => ({}))) as {
          code?: string
          message?: string
          next?: string
        }
        // A refusal (403) names its reason — an unverified or missing
        // email — so it goes to Access denied, with the error code choosing
        // the copy there (D8): "try again" would not help.
        if (resp.status === 403) {
          logout()
          const denied: AccessDeniedState = { code: body.code, message: body.message }
          navigate(AUTH_PATHS.accessDenied, { replace: true, state: denied })
          return
        }
        if (resp.ok && body.next === 'select_account') {
          navigate(AUTH_PATHS.selectAccount, { replace: true })
          return
        }
        returnToLoginWithAuthFailed()
      })
      .catch(returnToLoginWithAuthFailed)
  }, [navigate, logout])

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg-app)] text-[var(--text-tertiary)] text-[13px] gap-2">
      {t('oauthCallback.signingIn')}
    </div>
  )
}
